import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageHeader from '../components/PageHeader'
import MonthlyReport from '../components/capacity/MonthlyReport'
import StaffingMatrix from '../components/capacity/StaffingMatrix'
import StaffingMonthly from '../components/capacity/StaffingMonthly'
import StaffingWeeks from '../components/capacity/StaffingWeeks'
import StaffingChart from '../components/capacity/StaffingChart'
import CostRates from '../components/capacity/CostRates'
import { CostLockProvider } from '../components/capacity/CostLockContext'

/**
 * Capacity across the whole company: who is free, and how a month balances.
 *
 * The three tabs share one CostLockProvider so unlocking the costs once holds
 * while you move between the balance and the rates -- and drops the moment the
 * page is left, because the password lives in memory and nowhere else.
 *
 * `#activation` is kept as an alias of `#monthly` -- "Balance mensual" and
 * "Activacion de horas" used to be separate tabs and both links are out there
 * already; they now land on the same merged tab.
 *
 * Disponibilidad holds two views of the same staffing plan. "Por proyecto"
 * lays it out as a project x engineer matrix -- who is on what, and whose
 * total does not fit. "Por semana" lays the same hours out week by week and
 * is where they get rebalanced: expand somebody and their projects become
 * editable rows. Neither answers the other's question, so both stay.
 */
const HASHES = { '#monthly': 'monthly', '#activation': 'monthly', '#rates': 'rates' }

export default function Capacity() {
  const { t } = useTranslation('clevel')
  const [tab, setTab] = useState(HASHES[window.location.hash] || 'availability')
  const [freeView, setFreeView] = useState('matrix')   // matrix | weeks

  const go = (next) => {
    setTab(next)
    const hash = Object.entries(HASHES).find(([, v]) => v === next)?.[0] || ''
    history.replaceState(null, '', `${window.location.pathname}${hash}`)
  }

  const TABS = [
    ['availability', t('capacity.tabAvailability')],
    ['monthly', t('capacity.tabMonthly')],
    ['rates', t('capacity.tabRates')],
  ]

  return (
    <CostLockProvider>
      <div className="p-8">
        <PageHeader title={t('capacity.title')} subtitle={t('capacity.subtitle')} />

        <div className="flex items-center gap-1 border-b border-gray-200 mt-6 mb-5">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => go(key)}
              className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
                tab === key
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'availability' && (
          <div className="space-y-4">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs w-fit">
              {[['matrix', 'viewByProject'], ['weeks', 'viewByWeek'], ['byMonth', 'viewByMonth'],
                ['chart', 'viewChart']].map(([key, label]) => (
                <button key={key} onClick={() => setFreeView(key)}
                  className={`px-3 py-1.5 ${freeView === key
                    ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {t(`capacity.${label}`)}
                </button>
              ))}
            </div>
            {freeView === 'matrix' && <StaffingMatrix />}
            {freeView === 'weeks' && <StaffingWeeks />}
            {freeView === 'byMonth' && <StaffingMonthly />}
            {freeView === 'chart' && <StaffingChart />}
          </div>
        )}
        {tab === 'monthly' && <MonthlyReport />}
        {tab === 'rates' && <CostRates />}
      </div>
    </CostLockProvider>
  )
}
